"use client";

import { useState } from "react";
import { Share2 } from "@/components/icons";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { handleError } from "@/lib/handle-error";

export type PriceShareLinks = {
  pageUrl: string;
  imageUrl: string;
  imagePath: string;
  scriptSnippet: string;
  iframeSnippet: string;
  imageAlt: string;
};

type EmbedFormat = "script" | "iframe";

const EMBED_FORMATS: Array<{ value: EmbedFormat; label: string }> = [
  { value: "script", label: "Script" },
  { value: "iframe", label: "iframe" },
];

function EmbedDialog({
  links,
  open,
  onOpenChange,
}: {
  links: PriceShareLinks;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [format, setFormat] = useState<EmbedFormat>("script");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Put this chart on your site</DialogTitle>
        </DialogHeader>
        <SegmentedControl
          size="compact"
          aria-label="Embed format"
          value={format}
          onValueChange={setFormat}
          options={EMBED_FORMATS}
        />
        <CodeBlock
          value={format === "script" ? links.scriptSnippet : links.iframeSnippet}
          copyLabel="Embed code"
        />
      </DialogContent>
    </Dialog>
  );
}

async function copyLink(url: string) {
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  } catch (error) {
    handleError(error, {
      fallbackMessage: "Couldn't copy. Copy the address from your browser instead.",
    });
  }
}

export function ShareMenu({ links }: { links: PriceShareLinks }) {
  const [isEmbedOpen, setIsEmbedOpen] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <Share2 aria-hidden />
            Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => void copyLink(links.pageUrl)}>
            Copy link
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setIsEmbedOpen(true)}>
            Embed on your site
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a href={links.imagePath} target="_blank" rel="noopener">
              Chart image
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a href="#price-data">Download the data</a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <EmbedDialog
        links={links}
        open={isEmbedOpen}
        onOpenChange={setIsEmbedOpen}
      />
    </>
  );
}
